import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import {
  CheckCircle,
  Payment,
  AccountBalance,
  Receipt,
  Upload,
  Error as ErrorIcon,
  AccessTime,
  Phone,
  Email,
} from "@mui/icons-material";
import { useQuoteLink } from "../contexts/QuoteLinkContext";
import { QuoteHeader } from "../components/quoteLink/QuoteHeader";
import { PaymentForm } from "../components/quoteLink/PaymentForm";
import { useQuoteLinkService } from "../hooks/useQuoteLinkService";
import { usePhoneValidation } from "../hooks/usePhoneValidation";
import { usePublicSettings, getPublicSetting } from "../hooks/usePublicSettings.js";
import { logDebug } from "../shared/utils/logger.js";

/**
 * PaymentInstructionsPage - Page for viewing payment instructions and submitting payment proof
 * Mobile-first responsive design for payment flow
 */
const PaymentInstructionsPage = () => {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { quote, loading, error, actions, loadQuote } = useQuoteLink(tokenId);
  const { submitPaymentProof, validateOwnership } = useQuoteLinkService();
  const { isValidPhone, formatPhone } = usePhoneValidation();
  const { settings: publicSettings, loading: settingsLoading } = usePublicSettings();

  // Get settings from API with fallback to env vars
  const BUSINESS_NAME = getPublicSetting(publicSettings, "business.name", import.meta.env.VITE_BUSINESS_NAME || "");
  const CONTACT_PHONE = getPublicSetting(publicSettings, "contact.phone", import.meta.env.VITE_CONTACT_PHONE || "+27 67 674 4632");
  const CONTACT_EMAIL = getPublicSetting(publicSettings, "contact.email", import.meta.env.VITE_CONTACT_EMAIL || "support@bubakii.co.za");
  const BANK_NAME = getPublicSetting(publicSettings, "payments.bank.name", import.meta.env.VITE_BANK_NAME || "FNB");
  const BANK_BRANCH_CODE = getPublicSetting(publicSettings, "payments.bank.branchCode", import.meta.env.VITE_BANK_BRANCH_CODE || "");
  const BANK_ACCOUNT_NUMBER = getPublicSetting(publicSettings, "payments.bank.accountNumber", import.meta.env.VITE_BANK_ACCOUNT_NUMBER || "");
  const BANK_ACCOUNT_TYPE = getPublicSetting(publicSettings, "payments.bank.accountType", import.meta.env.VITE_BANK_ACCOUNT_TYPE || "");
  const BANK_ACCOUNT_HOLDER = getPublicSetting(publicSettings, "payments.bank.accountHolder", import.meta.env.VITE_BANK_ACCOUNT_HOLDER || BUSINESS_NAME || "");
  const MIN_DEPOSIT_PERCENT = getPublicSetting(publicSettings, "payments.deposit.minPercent", 20);

  // Get phone from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const phoneFromUrl = urlParams.get("phone");

  // Local state
  const [phone, setPhone] = useState(phoneFromUrl || "");
  const [phoneMismatch, setPhoneMismatch] = useState(false);
  const [ownershipChecking, setOwnershipChecking] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: "",
    paymentMethod: "eft",
    proofFiles: [],
    notes: "",
  });
  const [paymentSuccess, setPaymentSuccess] = useState(null); // { amountPaid, quoteAmount, balance, files }
  const [showAcceptedInfo, setShowAcceptedInfo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionTimeout, setSubmissionTimeout] = useState(null);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  // Initialize quote data when component mounts
  useEffect(() => {
    if (tokenId && !quote) {
      loadQuote(tokenId);
    }
  }, [tokenId, quote, loadQuote]);

  // Debounced ownership validation on phone change
  useEffect(() => {
    let timer;
    const run = async () => {
      try {
        if (!phone || !isValidPhone(phone)) {
          setPhoneMismatch(false);
          return;
        }
        setOwnershipChecking(true);
        const res = await validateOwnership(tokenId, formatPhone(phone));
        setPhoneMismatch(res?.verified === false);
      } catch (e) {
        setPhoneMismatch(e?.code === "PHONE_MISMATCH");
      } finally {
        setOwnershipChecking(false);
      }
    };
    timer = setTimeout(run, 400);
    return () => clearTimeout(timer);
  }, [phone, tokenId, isValidPhone, validateOwnership, formatPhone]);

  // Quote data changes
  useEffect(() => {}, [quote]);

  // If quote is accepted, auto-open a success/instructions modal and hide submit button
  useEffect(() => {
    if (quote?.status === "accepted" && !paymentSuccess) {
      setShowAcceptedInfo(true);
      setShowPaymentDialog(true);
    }
  }, [quote?.status, paymentSuccess]);

  // Handle negative flows and error states
  useEffect(() => {
    // Clear accepted info if quote status changes away from accepted
    if (quote?.status !== "accepted" && showAcceptedInfo) {
      setShowAcceptedInfo(false);
    }

    // Handle phone validation errors
    if (phone && !isValidPhone(phone) && !ownershipChecking) {
      setAlert({
        show: true,
        message: "Please enter a valid South African phone number.",
        severity: "warning",
      });
    }
  }, [
    quote?.status,
    showAcceptedInfo,
    phone,
    isValidPhone,
    ownershipChecking,
    phoneMismatch,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (submissionTimeout) {
        clearTimeout(submissionTimeout);
      }
    };
  }, [submissionTimeout]);

  // Clear any legacy phone-required errors by showing inline entry instead
  useEffect(() => {
    if (
      error === "phone_required" ||
      (error &&
        typeof error === "string" &&
        error.includes("Phone number is required"))
    ) {
      setAlert({
        show: true,
        message: "Please enter your phone number below.",
        severity: "info",
      });
    }
  }, [error]);

  // Phone state changes
  useEffect(() => {}, [phone]);

  // Update phone when URL changes
  useEffect(() => {
    if (phoneFromUrl && phoneFromUrl !== phone) {
      logDebug("PaymentInstructionsPage: phone from URL detected", {
        phoneFromUrl,
      });
      setPhone(phoneFromUrl);
    }
  }, [phoneFromUrl, phone]);

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    const formatted = new Intl.NumberFormat("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `ZAR ${formatted}`;
  };

  const defaultDepositAmount =
    Math.round(
      ((quote?.totalCost ?? quote?.estimatedCost ?? 0) *
        (MIN_DEPOSIT_PERCENT / 100) +
        Number.EPSILON) *
        100
    ) / 100;
  const quoteRequiredAmount = Number(quote?.deposit?.requiredAmount);
  const depositAmount = Number.isFinite(quoteRequiredAmount)
    ? quoteRequiredAmount
    : defaultDepositAmount;
  const depositWaived = depositAmount === 0;

  // Detect last successful payment submission from actions log
  const lastSubmitAction = Array.isArray(actions)
    ? [...actions].reverse().find((a) => a.action === "submit_payment")
    : null;
  const successParam =
    new URLSearchParams(location.search).get("success") === "1";

  // Show success state if payment was previously submitted or URL indicates success
  const shouldShowSuccess = lastSubmitAction || successParam;

  const handleSubmitPayment = async () => {
    // Clear any existing alerts
    setAlert({ show: false, message: "", severity: "info" });

    if (depositWaived) {
      setAlert({
        show: true,
        message:
          "No deposit or payment proof is required for this quote. Our team will confirm your booking.",
        severity: "info",
      });
      return;
    }

    const amountNum = Number(paymentData.amount);

    // Validation checks with specific error messages
    if (!paymentData.amount || Number.isNaN(amountNum) || amountNum <= 0) {
      setAlert({
        show: true,
        message: "Please enter the amount paid.",
        severity: "warning",
      });
      return;
    }

    // Enforce the quote-specific requirement, including admin overrides.
    if (amountNum < depositAmount) {
      setAlert({
        show: true,
        message: `Required deposit is ${formatCurrency(depositAmount)}. Please adjust the amount and try again.`,
        severity: "warning",
      });
      return;
    }

    if (!paymentData.proofFiles || paymentData.proofFiles.length === 0) {
      setAlert({
        show: true,
        message: "Please upload at least one proof file (receipt/screenshot).",
        severity: "warning",
      });
      return;
    }

    // Check if phone number is valid and owned
    if (!isValidPhone(phone) || phoneMismatch) {
      setAlert({
        show: true,
        message: phoneMismatch
          ? "This phone doesn't match the number that received this link."
          : "Enter a valid phone number to submit payment proof.",
        severity: "warning",
      });
      return;
    }

    // Check if quote is still in correct status
    if (quote?.status !== "accepted" && quote?.status !== "pending_payment") {
      setAlert({
        show: true,
        message:
          "Quote status has changed. Please refresh the page and try again.",
        severity: "error",
      });
      return;
    }

    setSubmitting(true);

    // Set a timeout for submission
    const timeout = setTimeout(() => {
      setAlert({
        show: true,
        message:
          "Submission is taking longer than expected. Please check your connection and try again.",
        severity: "warning",
      });
      setSubmitting(false);
    }, 30000); // 30 second timeout

    setSubmissionTimeout(timeout);

    try {
      const result = await submitPaymentProof(tokenId, phone, paymentData);

      // Clear timeout on success
      if (timeout) {
        clearTimeout(timeout);
        setSubmissionTimeout(null);
      }

      if (result.success) {
        const amountPaid = Number(paymentData.amount) || 0;
        const quoteAmount = Number(quote?.estimatedCost) || 0;
        const balance = Math.max(quoteAmount - amountPaid, 0);
        const files = Array.isArray(paymentData.proofFiles)
          ? paymentData.proofFiles
          : [];
        setPaymentSuccess({ amountPaid, quoteAmount, balance, files });
        // Ensure modal stays open and success content is visible
        setShowPaymentDialog(true);
        setTimeout(() => {
          const dialog = document.querySelector(
            '[role="dialog"] .MuiDialogContent-root'
          );
          if (typeof dialog?.scrollTo === "function") {
            dialog.scrollTo({ top: 0, behavior: "smooth" });
          }
        }, 0);
        // Do not navigate here; keep modal visible. We'll set the URL flag on Done.
        setPaymentData({
          amount: "",
          paymentMethod: "eft",
          proofFiles: [],
          notes: "",
        });
      } else {
        throw new Error(result.error || "Payment submission failed");
      }
    } catch (err) {
      // Clear timeout on error
      if (timeout) {
        clearTimeout(timeout);
        setSubmissionTimeout(null);
      }

      let errorMessage = "Failed to submit payment proof. Please try again.";

      // Handle specific error cases
      if (err.message?.includes("phone")) {
        errorMessage =
          "Phone number validation failed. Please check your number and try again.";
      } else if (err.message?.includes("file")) {
        errorMessage =
          "File upload failed. Please check your files and try again.";
      } else if (err.message?.includes("amount")) {
        errorMessage =
          "Invalid payment amount. Please check the amount and try again.";
      } else if (
        err.message?.includes("network") ||
        err.message?.includes("fetch")
      ) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (err.message?.includes("timeout")) {
        errorMessage =
          "Request timed out. Please check your connection and try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setAlert({
        show: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading payment instructions...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Payment Instructions Not Available
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/quote/${tokenId}`)}
              sx={{ mt: 2 }}
            >
              Back to Quote
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Check if quote is in correct status for payment
  if (quote?.status !== "accepted" && quote?.status !== "pending_payment") {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Quote Not Accepted
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You need to accept the quote before viewing payment instructions.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/quote/${tokenId}`)}
              sx={{ mt: 2 }}
            >
              Accept Quote
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      {/* Alert Messages */}
      {alert.show && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, show: false })}
          sx={{ position: "fixed", top: 16, left: 16, right: 16, zIndex: 1000 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Global mismatch banner */}
      {phoneMismatch && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          This phone doesn’t match the number that received this link. Please
          use the phone that received the WhatsApp/SMS.
        </Alert>
      )}

      <Container maxWidth="sm" sx={{ py: 2 }}>
        {/* Quote Header */}
        <QuoteHeader quote={quote} />

        {/* Payment Status */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <CheckCircle color="success" />
              <Typography variant="h6">
                {shouldShowSuccess
                  ? "Payment Proof Submitted"
                  : "Quote Accepted"}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {shouldShowSuccess
                ? "Your payment proof has been received and is being processed."
                : "Your quote has been accepted. Please complete payment to proceed with your order."}
            </Typography>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Summary
            </Typography>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body1">Quote Amount:</Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(quote?.estimatedCost || 0)}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body1">Quote ID:</Typography>
              <Typography variant="body2" color="text.secondary">
                {quote?.id}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Payment Instructions */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Instructions
            </Typography>

            {/* Inline Phone Number Field (no popup) */}
            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => {
                const v = e.target.value;
                setPhone(v);
                // Immediately gate actions until ownership check completes
                setPhoneMismatch(false);
                setOwnershipChecking(true);
              }}
              placeholder="+27123456789"
              error={
                (phone?.trim().length > 0 && !isValidPhone(phone)) ||
                phoneMismatch
              }
              helperText={
                phoneMismatch
                  ? "This phone doesn’t match the number that received this link."
                  : phone?.trim().length > 0 && !isValidPhone(phone)
                    ? "Enter a valid South African phone (e.g., +27123456789 or 0123456789)"
                    : ""
              }
              sx={{ mb: 2 }}
            />

            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: "primary.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                Bank Transfer Details:
              </Typography>
              <List dense>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <AccountBalance />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Bank: ${BANK_NAME}`}
                    secondary={`Account Name: ${BANK_ACCOUNT_HOLDER}`}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Receipt />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Account Number: ${BANK_ACCOUNT_NUMBER}`}
                    secondary={`Branch Code: ${BANK_BRANCH_CODE}${BANK_ACCOUNT_TYPE ? ` • ${BANK_ACCOUNT_TYPE}` : ""}`}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Payment />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Reference: ${quote?.id || "N/A"}`}
                    secondary={`Amount: ${formatCurrency(quote?.estimatedCost || 0)}`}
                  />
                </ListItem>
              </List>
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> The required deposit of{" "}
                <strong>{formatCurrency(depositAmount)}</strong> must be paid
                and cleared in our account before we can proceed with booking.
              </Typography>
            </Alert>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Please use the quote ID as your
                payment reference to ensure quick processing.
              </Typography>
            </Alert>

            {!showAcceptedInfo && depositWaived && (
              <Alert severity="success">
                No deposit or payment proof is required for this quote. Our
                team will confirm your booking.
              </Alert>
            )}

            {!showAcceptedInfo && !depositWaived && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Enter your phone number above. The submit button activates
                  once the number is valid and matches the quote owner.
                </Typography>

                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Upload />}
                    onClick={() => setShowPaymentDialog(true)}
                    fullWidth
                    sx={{ minHeight: 48 }}
                    disabled={
                      !isValidPhone(phone) || phoneMismatch || ownershipChecking
                    }
                    title={
                      !isValidPhone(phone)
                        ? "Enter a valid phone number to continue"
                        : phoneMismatch
                          ? "This phone doesn't match the number that received this link."
                          : undefined
                    }
                  >
                    Submit Payment Proof
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Phone />}
                    onClick={() => window.open(`tel:${CONTACT_PHONE.replace(/[\s\-()]/g, "")}`)}
                    fullWidth
                    sx={{ minHeight: 48 }}
                  >
                    Call {CONTACT_PHONE}
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Email />}
                    onClick={() => window.open(`mailto:${CONTACT_EMAIL}`)}
                    fullWidth
                    sx={{ minHeight: 48 }}
                  >
                    Email {CONTACT_EMAIL}
                  </Button>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Status History */}
        {actions && actions.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment History
              </Typography>
              <Stack spacing={1}>
                {actions
                  .filter(
                    (action) =>
                      action.action === "submit_payment" ||
                      action.action === "accept"
                  )
                  .slice(0, 5)
                  .map((action, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1}>
                      <AccessTime fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {action.actionSummary} -{" "}
                        {new Date(action.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Payment Proof Submission Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => {
          // Prevent closing during submission
          if (submitting) return;
          setShowPaymentDialog(false);
          setShowAcceptedInfo(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentSuccess
            ? "Payment Proof Submitted"
            : showAcceptedInfo
              ? "Quote Accepted"
              : "Submit Payment Proof"}
        </DialogTitle>
        <DialogContent>
          {!paymentSuccess &&
            !showAcceptedInfo &&
            (!isValidPhone(phone) || phoneMismatch) && (
              <Alert
                severity={phoneMismatch ? "error" : "warning"}
                sx={{ mb: 2 }}
              >
                {phoneMismatch
                  ? "This phone doesn't match the number that received this link."
                  : "Please verify your phone number to enable submission."}
              </Alert>
            )}
          {!paymentSuccess &&
            !showAcceptedInfo &&
            alert.show &&
            alert.severity !== "success" && (
              <Alert severity={alert.severity} sx={{ mb: 2 }}>
                {alert.message}
              </Alert>
            )}
          {paymentSuccess ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Thank you. Your payment proof has been received.
              </Alert>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Payment Summary
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Amount Paid</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(paymentSuccess.amountPaid)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Quote Total</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(paymentSuccess.quoteAmount)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Balance</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(paymentSuccess.balance)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              <Alert severity="info">
                We will verify your payment within 1–3 business days. You will
                receive a confirmation message with next steps.
              </Alert>
            </Box>
          ) : showAcceptedInfo ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Quote accepted. Please review payment instructions below.
              </Alert>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Summary
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Quote Total</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(quote?.estimatedCost || 0)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">
                        Required Deposit
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(depositAmount)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              <Alert severity="info">
                Use the quote ID as your reference. You can submit proof later
                once payment is made.
              </Alert>
            </Box>
          ) : (
            <Box>
              <PaymentForm
                paymentData={paymentData}
                onPaymentDataChange={setPaymentData}
                quoteAmount={quote?.estimatedCost}
              />

              {/* Retry button for failed submissions */}
              {alert.show && alert.severity === "error" && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Clear timeout if exists
                      if (submissionTimeout) {
                        clearTimeout(submissionTimeout);
                        setSubmissionTimeout(null);
                      }
                      setAlert({ show: false, message: "", severity: "info" });
                      setSubmitting(false);
                    }}
                    disabled={submitting}
                  >
                    Clear Error & Retry
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!paymentSuccess && !showAcceptedInfo ? (
            <>
              <Button onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitPayment}
                variant="contained"
                disabled={submitting || !isValidPhone(phone) || phoneMismatch}
                startIcon={
                  submitting ? <CircularProgress size={20} /> : <Upload />
                }
              >
                {submitting ? "Submitting..." : "Submit Proof"}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                setShowPaymentDialog(false);
                setPaymentSuccess(null);
                setShowAcceptedInfo(false);
                window.requestAnimationFrame(() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  setAlert({
                    show: true,
                    message: paymentSuccess
                      ? "Payment proof submitted successfully! We'll verify it shortly."
                      : "Quote accepted. Review payment instructions in your email/WhatsApp.",
                    severity: "success",
                  });
                });
                const sp = new URLSearchParams(location.search);
                sp.set("success", "1");
                navigate(
                  { pathname: location.pathname, search: sp.toString() },
                  { replace: true }
                );
              }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentInstructionsPage;
